import React, { useCallback, useState } from 'react';
import { Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import { assessImageQuality, QualityResult } from 'helpers/imageQuality';
import CaptureCoach from './CaptureCoach';
import QualityGateOverlay from './QualityGateOverlay';
import UploadFallback from './UploadFallback';
import styles from './CameraCapture.module.scss';

export interface CaptureMeta {
    orientation?: 'portrait' | 'landscape' | 'unknown';
    source: 'camera' | 'upload';
}

interface CameraCaptureProps {
    opened: boolean;
    onClose: () => void;
    // Single exit point: hands a ready-to-upload File back to the page.
    onCapture: (file: File, meta: CaptureMeta) => void;
}

type Captured = { file: File; url: string; source: CaptureMeta['source'] };

const CameraCapture = ({ opened, onClose, onCapture }: CameraCaptureProps) => {
    // Desktop => centered modal + drag-drop. Mobile => full-screen overlay with
    // the pre-capture coach + native camera hand-off.
    const isDesktop = useMediaQuery('(min-width: 62em)') ?? false;

    const [mode, setMode] = useState<'capture' | 'review'>('capture');
    const [captured, setCaptured] = useState<Captured | null>(null);
    const [quality, setQuality] = useState<QualityResult | null>(null);

    const clearCaptured = useCallback(() => {
        setCaptured((prev) => {
            if (prev) URL.revokeObjectURL(prev.url); // free the preview URL (not the File)
            return null;
        });
        setQuality(null);
    }, []);

    const reset = useCallback(() => {
        setMode('capture');
        clearCaptured();
    }, [clearCaptured]);

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    // A full-resolution File straight from the OS camera / file picker. We keep
    // the original File (no re-encoding) and only make an object URL for preview
    // and the quality check.
    const handleFile = useCallback((file: File, source: CaptureMeta['source']) => {
        const url = URL.createObjectURL(file);
        setCaptured({ file, url, source });
        setQuality(null);
        setMode('review');
        // Assess the real (full-res) file; stay permissive if analysis fails.
        assessImageQuality(file)
            .then(setQuality)
            .catch(() => setQuality({ pass: true, blurScore: NaN, brightness: NaN, reasons: [] }));
    }, []);

    const handleAccept = useCallback(() => {
        if (!captured) return;
        onCapture(captured.file, { orientation: 'unknown', source: captured.source });
        handleClose();
    }, [captured, onCapture, handleClose]);

    const handleRetake = useCallback(() => {
        clearCaptured();
        setMode('capture');
    }, [clearCaptured]);

    if (!opened) return null;

    const review = captured && (
        <QualityGateOverlay
            dataUrl={captured.url}
            result={quality}
            onAccept={handleAccept}
            onRetake={handleRetake}
        />
    );

    // ---- Desktop: centered modal with drag-drop upload (+ quality review) ----
    if (isDesktop) {
        return (
            <Modal opened={opened} onClose={handleClose} title="Add Aperture Photo" centered size="lg">
                <div className={styles.body}>
                    {mode === 'review' && captured
                        ? review
                        : <UploadFallback onFile={(f) => handleFile(f, 'upload')} />}
                </div>
            </Modal>
        );
    }

    // ---- Mobile: full-screen overlay — coach, then review ----
    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={styles.overlayHeader}>
                <span className={styles.overlayTitle}>
                    {mode === 'review' ? 'Review Photo' : 'Add Aperture Photo'}
                </span>
                <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
                    <IconX size={22} />
                </button>
            </div>

            {mode === 'review' && captured
                ? <div className={styles.reviewBody}>{review}</div>
                : <CaptureCoach onFile={(f) => handleFile(f, 'camera')} />}
        </div>
    );
};

export default CameraCapture;
